import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LandingFooter from '../nord-pass-landing-page/components/LandingFooter';
import CommentTable from './components/CommentTable';
import ModerationFilterPanel from './components/ModerationFilterPanel';
import ModerationStatsBar from './components/ModerationStatsBar';

const AdminCommentModerationDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', search: '' });
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, flagged: 0 });
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch comments with post titles
      const { data: commentsData } = await supabase?.from('comments')?.select('*, blog_posts(title)')?.order('created_at', { ascending: false });

      const enriched = (commentsData || [])?.map(c => ({
        ...c,
        post_title: c?.blog_posts?.title || '—'
      }));

      // Compute stats
      const s = { pending: 0, approved: 0, rejected: 0, flagged: 0 };
      enriched?.forEach(c => { if (s?.[c?.status] !== undefined) s[c.status]++; });
      setStats(s);
      setComments(enriched);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchComments, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchComments]);

  const handleModerate = async (commentId, action) => {
    if (action === 'delete') {
      await supabase?.from('comments')?.delete()?.eq('id', commentId);
    } else {
      await supabase?.from('comments')?.update({ status: action })?.eq('id', commentId);
    }
    fetchComments();
  };

  const handleBulkModerate = async (ids, action) => {
    if (action === 'delete') {
      await supabase?.from('comments')?.delete()?.in('id', ids);
    } else {
      await supabase?.from('comments')?.update({ status: action })?.in('id', ids);
    }
    fetchComments();
  };

  const filteredComments = comments?.filter(c => {
    const matchStatus = filters?.status === 'all' || c?.status === filters?.status;
    const q = filters?.search?.toLowerCase() || '';
    const matchSearch = !q || c?.author_name?.toLowerCase()?.includes(q) || c?.author_email?.toLowerCase()?.includes(q) || c?.content?.toLowerCase()?.includes(q) || c?.post_title?.toLowerCase()?.includes(q);
    return matchStatus && matchSearch;
  });

  return (
    <>
      <Helmet>
        <title>Kommentarmoderering — NordPass Admin</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet" />
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Nav */}
        <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(5,10,18,0.97)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
            <a href="/nord-pass-landing-page" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <div style={{ width: '28px', height: '28px', background: '#3B6FD4', clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }} />
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1rem', fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em' }}>NordPass</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#E9C46A', letterSpacing: '0.15em', border: '1px solid rgba(233,196,74,0.3)', padding: '4px 10px' }}>ADMIN</span>
              <a href="/nord-pass-admin-dashboard" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: 'rgba(238,240,242,0.5)', textDecoration: 'none' }}>← Admin Dashboard</a>
              <button onClick={() => setAutoRefresh(!autoRefresh)} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: autoRefresh ? '#2EC4A0' : 'rgba(138,149,163,0.5)', background: 'none', border: `1px solid ${autoRefresh ? 'rgba(46,196,160,0.3)' : 'rgba(255,255,255,0.08)'}`, padding: '6px 12px', cursor: 'pointer', letterSpacing: '0.1em' }}>
                {autoRefresh ? '⟳ AUTO PÅ' : '⟳ AUTO AV'}
              </button>
              <button onClick={fetchComments} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: '#EEF0F2', background: 'rgba(59,111,212,0.15)', border: '1px solid rgba(59,111,212,0.3)', padding: '8px 16px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>OPPDATER</button>
            </div>
          </div>
        </nav>

        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '48px 5%' }}>
          {/* Hero header */}
          <div style={{ marginBottom: '40px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-20px', left: '-10px', fontFamily: 'Impact, sans-serif', fontSize: '8rem', color: 'rgba(255,255,255,0.02)', letterSpacing: '-0.04em', lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>MOD</div>
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#3B6FD4', letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 8px', position: 'relative' }}>ADMIN / KOMMENTARER</p>
            <h1 style={{ fontFamily: 'Impact, sans-serif', fontSize: '3rem', color: '#EEF0F2', letterSpacing: '-0.02em', textTransform: 'uppercase', margin: '0 0 8px', position: 'relative' }}>KOMMENTARMODERERING</h1>
            <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.45)', fontSize: '1rem', margin: 0, position: 'relative' }}>Administrer og moderer brukerkommentarer på tvers av alle bloggartikler</p>
          </div>

          {/* Stats bar */}
          <ModerationStatsBar stats={stats} totalCount={comments?.length} />

          {/* Main layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px', alignItems: 'start' }}>
            {/* Filter panel */}
            <div style={{ position: 'sticky', top: '88px' }}>
              <ModerationFilterPanel filters={filters} onChange={setFilters} stats={stats} />
            </div>

            {/* Table */}
            <div style={{ background: '#0A1520', border: '1px solid rgba(255,255,255,0.06)', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.8rem', color: 'rgba(238,240,242,0.5)', margin: 0, letterSpacing: '0.05em' }}>
                  {loading ? 'Laster...' : `${filteredComments?.length} kommentar${filteredComments?.length !== 1 ? 'er' : ''} vist`}
                </p>
                {filters?.status !== 'all' && (
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: '#3B6FD4', border: '1px solid rgba(59,111,212,0.3)', padding: '3px 8px', letterSpacing: '0.1em' }}>FILTER: {filters?.status?.toUpperCase()}</span>
                )}
              </div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '64px' }}>
                  <p style={{ fontFamily: 'Impact, sans-serif', fontSize: '2rem', color: 'rgba(255,255,255,0.06)', letterSpacing: '0.1em' }}>LASTER...</p>
                </div>
              ) : (
                <CommentTable comments={filteredComments} onModerate={handleModerate} onBulkModerate={handleBulkModerate} />
              )}
            </div>
          </div>
        </div>

        <LandingFooter />
      </div>
    </>
  );
};

export default AdminCommentModerationDashboard;
